import { useRef, useCallback, useEffect } from 'react';

/**
 * Hook audio de l'intro cinématique DNA-24.
 *
 * TOUT l'audio passe par la Web Audio API (un seul `AudioContext`) :
 *  - Voix (ElevenLabs, MP3) et bruitages (grésillement, crash) sont préchargés
 *    et décodés en `AudioBuffer`, puis joués via des `AudioBufferSourceNode`.
 *  - Bips ECG : générés en direct par oscillateur.
 *
 * Avantage : compatibilité universelle (PC + mobile iOS/Android). Les balises
 * HTML5 `<audio>` étaient bloquées par la politique « autoplay » sur mobile
 * lorsqu'elles étaient déclenchées hors d'un geste utilisateur (setTimeout).
 *
 * `unlock()` doit être appelé depuis un geste utilisateur (clic cookies / « Activer le son »).
 */

const AUDIO_BASE = `${process.env.PUBLIC_URL || ''}/dna/audio`;

// Sons préchargés au déblocage (voix par langue + bruitages).
const VOICE_KEYS = ['reassure1', 'reassure2', 'thanks', 'world', 'final1', 'final2', 'final3'];
const SFX_FILES = ['static.wav', 'crash.mp3'];

const useIntroAudio = ({ lang, enabled }) => {
  const ctxRef = useRef(null);
  const buffersRef = useRef({}); // url -> AudioBuffer
  const loadingRef = useRef({}); // url -> Promise (déduplication)
  const activeRef = useRef([]); // sources en cours : { id, src }
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  // Charge + décode un fichier audio en AudioBuffer (mémorisé).
  const loadBuffer = useCallback((url) => {
    if (buffersRef.current[url]) return Promise.resolve(buffersRef.current[url]);
    if (loadingRef.current[url]) return loadingRef.current[url];
    const ctx = ctxRef.current;
    if (!ctx) return Promise.reject(new Error('AudioContext non initialisé'));
    const p = fetch(url)
      .then((r) => r.arrayBuffer())
      .then((ab) => ctx.decodeAudioData(ab))
      .then((buf) => {
        buffersRef.current[url] = buf;
        return buf;
      });
    loadingRef.current[url] = p;
    return p;
  }, []);

  // Débloque le contexte audio (geste utilisateur) et précharge tous les sons.
  const unlock = useCallback(() => {
    if (!ctxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) ctxRef.current = new Ctx();
    }
    const ctx = ctxRef.current;
    if (!ctx) return;
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    VOICE_KEYS.forEach((k) => loadBuffer(`${AUDIO_BASE}/${lang}/${k}.mp3`).catch(() => {}));
    SFX_FILES.forEach((f) => loadBuffer(`${AUDIO_BASE}/sfx/${f}`).catch(() => {}));
  }, [lang, loadBuffer]);

  // Joue un AudioBuffer (chargé ou à charger) avec un volume donné.
  const playUrl = useCallback(
    (url, volume, id) => {
      const ctx = ctxRef.current;
      if (!enabledRef.current || !ctx) return;
      const start = (buf) => {
        if (!buf || !enabledRef.current) return;
        const src = ctx.createBufferSource();
        const gain = ctx.createGain();
        gain.gain.value = volume;
        src.buffer = buf;
        src.connect(gain).connect(ctx.destination);
        const entry = { id, src };
        activeRef.current.push(entry);
        src.onended = () => {
          activeRef.current = activeRef.current.filter((e) => e !== entry);
        };
        src.start();
      };
      const buf = buffersRef.current[url];
      if (buf) start(buf);
      else loadBuffer(url).then(start).catch(() => {});
    },
    [loadBuffer]
  );

  // Joue une réplique vocale.
  const playVoice = useCallback(
    (key) => playUrl(`${AUDIO_BASE}/${lang}/${key}.mp3`, 1, `voice:${key}`),
    [lang, playUrl]
  );

  // Effet sonore (grésillement, crash…).
  const playSfx = useCallback(
    (file, volume = 1) => playUrl(`${AUDIO_BASE}/sfx/${file}`, volume, file),
    [playUrl]
  );

  // Coupe un SFX précis (ex. grésillement coupé par le crash).
  const stopSfx = useCallback((file) => {
    activeRef.current
      .filter((e) => e.id === file)
      .forEach((e) => {
        try {
          e.src.stop();
        } catch (err) {
          /* source déjà arrêtée */
        }
      });
  }, []);

  // Bip d'électrocardiogramme (oscillateur court avec enveloppe douce).
  const beep = useCallback(() => {
    const ctx = ctxRef.current;
    if (!enabledRef.current || !ctx) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, now);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.14, now + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.16);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.18);
  }, []);

  // Coupe toutes les sources en cours (fermeture / skip).
  const stopAll = useCallback(() => {
    activeRef.current.forEach((e) => {
      try {
        e.src.stop();
      } catch (err) {
        /* source déjà arrêtée */
      }
    });
    activeRef.current = [];
  }, []);

  useEffect(() => stopAll, [stopAll]);

  return { unlock, playVoice, playSfx, stopSfx, beep, stopAll };
};

export default useIntroAudio;
